import React, { ReactNode, useCallback, useMemo } from 'react'
import { Home as HomeIcon, Settings, Award, FileText, Users, User, AtSign, LogOut } from 'react-feather'
import { connect } from 'react-redux'
import clsx from 'clsx'
import { useNavigate, useLocation } from 'react-router-dom'
import { Icon as IconifyIcon } from '@iconify/react'
import { OrganizationData, UserData } from '../types'
import { clearAuthState } from '../store/slices'

interface PrivateLayoutOwnProps {
  children?: ReactNode
}

interface PrivateLayoutStateProps {
  user: UserData
  organization: OrganizationData
}

interface PrivateLayoutDispatchProps {
  clearAuthState: () => void
}

type PrivateLayoutProps = PrivateLayoutOwnProps & PrivateLayoutStateProps & PrivateLayoutDispatchProps

const PrivateLayout = ({ children, clearAuthState, user, organization }: PrivateLayoutProps): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()

  const logout = useCallback(() => {
    clearAuthState()
    navigate('/login')
  }, [])

  const onVerticalNavbarToggleClicked = useCallback(() => {
    document.documentElement.classList.toggle('navbar-vertical-collapsed')
  }, [])

  const isFunder = useMemo(() => {
    return user && user.roles.includes('ROLE_FUNDER')
  }, [user])

  return (
    <main className="main" id="top">
      <nav className="navbar navbar-vertical navbar-expand-lg">
        <div className="collapse navbar-collapse" id="navbarVerticalCollapse">
          <div className="navbar-vertical-content">
            <ul className="navbar-nav flex-column" id="navbarVerticalNav">
              <li className="nav-item">
                <div className="nav-item-wrapper">
                  <button
                    className={clsx('btn-link nav-link label-1', {
                      active: location.pathname.startsWith('/dashboard'),
                    })}
                    role="button"
                    data-bs-toggle=""
                    aria-expanded="false"
                    onClick={() => navigate('/dashboard')}
                  >
                    <div className="d-flex align-items-center">
                      <span className="nav-link-icon">
                        <HomeIcon size={16} />
                      </span>
                      <span className="nav-link-text-wrapper">
                        <span className="nav-link-text">Dashboard</span>
                      </span>
                    </div>
                  </button>
                </div>
              </li>
              {/*
                <li className="nav-item">
                  <div className="nav-item-wrapper">
                    <button
                      className={clsx('btn-link nav-link label-1', {
                        active: location.pathname.startsWith('/settings'),
                      })}
                      role="button"
                      data-bs-toggle=""
                      aria-expanded="false"
                      onClick={() => navigate('/settings')}
                    >
                      <div className="d-flex align-items-center">
                        <span className="nav-link-icon">
                          <Settings size={16} />
                        </span>
                        <span className="nav-link-text-wrapper">
                          <span className="nav-link-text">Settings</span>
                        </span>
                      </div>
                    </button>
                  </div>
                </li>
                */}

              <li className="nav-item">
                <p className="navbar-vertical-label">Reporting</p>
                <hr className="navbar-vertical-line" />
                <div className="nav-item-wrapper">
                  <button
                    className={clsx('btn-link nav-link label-1', {
                      active: location.pathname.startsWith('/grants'),
                    })}
                    role="button"
                    data-bs-toggle=""
                    aria-expanded="false"
                    onClick={() => navigate('/grants')}
                  >
                    <div className="d-flex align-items-center">
                      <span className="nav-link-icon">
                        <Award size={16} />
                      </span>
                      <span className="nav-link-text-wrapper">
                        <span className="nav-link-text">Grants</span>
                      </span>
                    </div>
                  </button>
                </div>
                {isFunder ? (
                  <>
                    <div className="nav-item-wrapper">
                      <button
                        className={clsx('btn-link nav-link label-1', {
                          active: location.pathname.startsWith('/works'),
                        })}
                        role="button"
                        data-bs-toggle=""
                        aria-expanded="false"
                        onClick={() => navigate('/works')}
                      >
                        <div className="d-flex align-items-center">
                          <span className="nav-link-icon">
                            <FileText size={16} />
                          </span>
                          <span className="nav-link-text-wrapper">
                            <span className="nav-link-text">Work Products</span>
                          </span>
                        </div>
                      </button>
                    </div>

                    {/*
                        <div className="nav-item-wrapper">
                          <button
                            className={clsx('btn-link nav-link label-1', {
                              active: location.pathname.startsWith('/researchers'),
                            })}
                            role="button"
                            data-bs-toggle=""
                            aria-expanded="false"
                            onClick={() => navigate('/researchers')}
                          >
                            <div className="d-flex align-items-center">
                              <span className="nav-link-icon">
                                <Users size={16} />
                              </span>
                              <span className="nav-link-text-wrapper">
                                <span className="nav-link-text">Researcher</span>
                              </span>
                            </div>
                          </button>
                        </div>
                      */}
                  </>
                ) : null}
              </li>
            </ul>
          </div>
        </div>
        <div className="navbar-vertical-footer">
          <button
            className="btn navbar-vertical-toggle border-0 fw-semi-bold w-100 white-space-nowrap d-flex align-items-center"
            onClick={onVerticalNavbarToggleClicked}
          >
            <IconifyIcon icon="uil:left-arrow-to-left" className="uil-left-arrow-to-left fs-0" />
            <IconifyIcon icon="uil:arrow-from-right" className="uil-arrow-from-right fs-0" />
            <span className="navbar-vertical-footer-text ms-2">Collapsed View</span>
          </button>
        </div>
      </nav>
      <nav
        className="navbar navbar-top fixed-top navbar-slim justify-content-between navbar-expand-lg"
        id="navbarComboSlim"
        data-navbar-top="combo"
        data-move-target="#navbarVerticalNav"
      >
        <div className="navbar-logo">
          <button
            className="btn navbar-toggler navbar-toggler-humburger-icon hover-bg-transparent"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarVerticalCollapse"
            aria-controls="navbarVerticalCollapse"
            aria-expanded="false"
            aria-label="Toggle Navigation"
          >
            <span className="navbar-toggle-icon">
              <span className="toggle-line"></span>
            </span>
          </button>
          <button className="btn-link navbar-brand navbar-brand" onClick={() => navigate('/')}>
            <span className="fas fa-spa me-1"></span>
            grant <span className="text-1000 d-none d-sm-inline">impact reporting</span>
          </button>
        </div>
        <ul className="navbar-nav navbar-nav-icons flex-row">
          {/*
            <li className="nav-item">
              <div className="theme-control-toggle fa-ion-wait pe-2 theme-control-toggle-slim">
                <input
                  className="form-check-input ms-0 theme-control-toggle-input"
                  id="themeControlToggle"
                  type="checkbox"
                  data-theme-control="phoenixTheme"
                  value="dark"
                />
                <label
                  className="mb-0 theme-control-toggle-label theme-control-toggle-light"
                  htmlFor="themeControlToggle"
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  title="Switch theme"
                >
                  <span className="icon me-1 d-none d-sm-block" data-feather="moon"></span>
                  <span className="fs--1 fw-bold">Dark</span>
                </label>
                <label
                  className="mb-0 theme-control-toggle-label theme-control-toggle-dark"
                  htmlFor="themeControlToggle"
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  title="Switch theme"
                >
                  <span className="icon me-1 d-none d-sm-block" data-feather="sun"></span>
                  <span className="fs--1 fw-bold">Light</span>
                </label>
              </div>
            </li>
            */}

          <li className="nav-item dropdown">
            <a
              className="nav-link lh-1 pe-0 white-space-nowrap"
              id="navbarDropdownUser"
              href="#!"
              role="button"
              data-bs-toggle="dropdown"
              aria-haspopup="true"
              data-bs-auto-close="outside"
              aria-expanded="false"
            >
              {user?.firstName} <span className="fa-solid fa-chevron-down fs--2"></span>
            </a>
            <div
              className="dropdown-menu dropdown-menu-end navbar-dropdown-caret py-0 dropdown-profile shadow border border-300"
              aria-labelledby="navbarDropdownUser"
            >
              <div className="card position-relative border-0">
                <div className="overflow-auto scrollbar" style={{ height: 'auto' }}>
                  <ul className="nav d-flex flex-column mb-2 pb-1">
                    <li className="nav-item">
                      <button className="btn-link nav-link px-3">
                        <User size={16} className="text-success-300 me-2" />
                        <span>{`${user?.firstName} ${user?.lastName}`}</span>
                      </button>
                    </li>
                    <li className="nav-item">
                      <button className="btn-link nav-link px-3">
                        <AtSign size={16} className="text-success-300 me-2" />
                        <h6 className="mt-2 text-black">{organization?.name}</h6>
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className="btn-link nav-link px-3"
                        onClick={() => {
                          logout()
                        }}
                      >
                        <LogOut size={16} className="text-danger-500 me-2" />
                        Sign out
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </nav>
      {children}
    </main>
  )
}

const mapStateToProps = (state: any): PrivateLayoutStateProps => {
  return {
    user: state.auth.user,
    organization: state.auth.organization,
  }
}

const mapDispatchToProps = (dispatch: any): PrivateLayoutDispatchProps => {
  return {
    clearAuthState: () => dispatch(clearAuthState()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PrivateLayout)
